import PlanSelectionView from "./PlanSelectionView";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import styles from "./RecommendedPlan.module.css";
import { useAgentStore } from "../../Store/agentDetailStore";
import { getAgentById as fetchAgentById } from "../../Store/apiStore";
import decodeToken from "../../lib/decodeToken";
import { trackEvent, stashPendingPurchase } from "../../lib/analytics";
import { useLocation, useNavigate } from "react-router-dom";
import NewModal from "../NewModal/NewModal";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import detectPlatform from "../../utils/detectPlatform";
import { isMobileAppSurface } from "../../contexts/GlobalCallContext";
import { isAgentRecentlyPaid } from "../../lib/paymentGuard";
import { setPaymentCancelReturn } from "../PaymentCancel/PaymentCancel";
import GoLiveVariant from "../GoLivePayg/GoLiveVariant";
import { safeBackPath } from "../../utils/safeBackPath";
import { isEmbeddedCheckoutEnabled } from "../../lib/embeddedCheckoutFlag";
import { useCheckoutStore } from "../../Store/checkoutStore";
import { isAppSumoUser, isIndiaSite } from "../../utils/apiBackend";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Plans we render (in order). Anything else returned by /products is filtered
// out. Each entry maps a Stripe product NAME (case-insensitive) to the
// presentation bits that are NOT in the product feed: icon, summary line,
// and whether it's the recommended card.
const PLAN_DEFS = {
    starter: {
        order: 0,
        icon: "/svg/starter-icon_new.svg",
        summary: "1 language | Free VoIP number | Basic AI analysis",
    },
    scaler: {
        order: 1,
        icon: "/svg/scaler-icon_new.svg",
        summary: "Multi-lingual | CRM Sync | Sentiment Analysis",
        recommended: true,
    },
    growth: {
        order: 2,
        icon: "/svg/growth-icon_icon.svg",
        summary: "Rollover Mins. | Dedicated Manager | AI Training",
    },
};

const DEFAULT_SELECTED = "scaler";

/*
 * Back target for this screen: whatever the caller put in
 * `location.state.locationPath` (FastAgentDetails.handleUpgradeClick sets it),
 * validated — see utils/safeBackPath. `avoid` stops a stale state value from
 * pointing Back at this same screen, which would look like a dead button.
 *
 * Module scope on purpose: the component has an early `return <GoLivePayg />`
 * for non-paid native users, and the popstate effect below still runs in that
 * case. A helper declared after that return would be in the temporal dead zone
 * when the effect fired — a ReferenceError, i.e. the blank screen we're
 * guarding against.
 */
const resolveBackPath = (raw) =>
    safeBackPath(raw, { avoid: "/recommended-plan" });

// Promo CTA appears this many ms after the plans view first renders.
// Spec: "10 sec baad dikhegi page load hone ke".
const PROMO_DELAY_MS = 10_000;

// 21-day Starter trial fixed terms — these match the trial screen copy.
const TRIAL_DAYS = 21;
const TRIAL_MINUTES = 150;
const TRIAL_PLAN_NAME = "starter";
const TRIAL_ACTIVATION_PRICE_LABEL = "$20";

// Feature comparison table — rendered inline when "See full feature
// comparison" is expanded. Mirrors the table in Figma: ✓ / "—" / a literal
// string per cell.
const COMPARISON_FEATURES = [
    {
        icon: "/svg/Phone-call_new.svg",
        title: "24/7 Call Answering",
        desc: "Every call answered on the first ring, 24/7, in a natural human voice",
    },
    { label: "AI Voice & Chat", cells: { starter: true, scaler: true, growth: true } },
    { label: "5-minute setup", cells: { starter: true, scaler: true, growth: true } },
    {
        icon: "/svg/calander_icon.svg",
        title: "Automatic Appointment Booking",
        desc: "Books directly into your Google Calendar during the live call",
    },
    {
        icon: "/svg/calander_icon.svg",
        title: "Call Triage & Routing",
        desc: "Sales calls go to you. Admin to office. You define the rules",
    },
    { label: "Calendar Booking", cells: { starter: true, scaler: true, growth: true } },
    {
        icon: "/svg/calander_icon.svg",
        title: "Spam Call Blocking",
        desc: "Identifies and blocks telemarketers before they waste your time",
    },
    { label: "Language", cells: { starter: "1", scaler: "Multi", growth: "Multi" } },
    { label: "Free VoIP number", cells: { starter: true, scaler: true, growth: true } },
    { label: "Basic AI analysis", cells: { starter: true, scaler: true, growth: true } },
];

const CAPABILITIES = COMPARISON_FEATURES.filter(
    (feature) => feature.icon && feature.title && feature.desc
);

const COMPARISON_ROWS = COMPARISON_FEATURES.filter(
    (feature) => feature.label && feature.cells
);

const COMPARISON_COLUMNS = ["starter", "scaler", "growth"];

const COUNTRY_CURRENCY_MAP = {
    US: "usd",
    CA: "cad",
    AU: "aud",
    GB: "gbp",
    IN: "inr",
};

const getCurrencySymbol = (code) => {
    try {
        return (
            new Intl.NumberFormat("en", {
                style: "currency",
                currency: (code || "usd").toUpperCase(),
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            })
                .formatToParts(1)
                .find((part) => part.type === "currency")?.value || "$"
        );
    } catch {
        return "$";
    }
};

const RecommendedPlan = () => {
    const location = useLocation();
    // Programmatic nav for the upgrade-flow back-handler + top-tier
    // alert flow added below. (useLocation alone wasn't enough; both
    // flows need to navigate(replace:true) to /fast-agent-detail.)
    const navigate = useNavigate();
    // 'plans' is the default surface for /recommended-plan — the Choose Your
    // Plan grid, shown straight away.
    //
    // It used to open on the 'trial' pitch screen ("Here is what {agent} can do
    // for {business}") with a Choose Your Plan CTA that flipped to the grid.
    // That was one extra tap in front of the only thing this route exists to
    // do, so the grid is what everyone lands on now, whichever way they arrive
    // — Go Live, an upgrade tap, a lifecycle email, a cancelled checkout.
    //
    // The pitch view is still reachable with ?mode=trial (see the effect
    // below), so nothing that deliberately asks for it is broken. The $20
    // trial Stripe checkout was never on the pitch screen anyway: it lives on
    // the plans view's cross-icon modal.
    const [trialModalOpen, setTrialModalOpen] = useState(false);
    const [view, setView] = useState("plans");
    const [userCity, setUserCity] = useState("");
    const [billingInterval, setBillingInterval] = useState("month"); // 'month' | 'year'
    const [selectedPlanId, setSelectedPlanId] = useState(DEFAULT_SELECTED);

    // Promo CTA: hidden until 10s has elapsed on the plans view.
    const [promoVisible, setPromoVisible] = useState(false);
    const [showComparison, setShowComparison] = useState(false);

    // Live data state (mirrors /plan).
    const [products, setProducts] = useState([]);
    const [userCurrency, setUserCurrency] = useState("usd");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Action latches so a double-tap can't open two Stripe sessions.
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [trialLoading, setTrialLoading] = useState(false);
    const promoTimerRef = useRef(null);
    const comparisonRef = useRef(null);

    const token = localStorage.getItem("token") || "";
    const decodeTokenData = decodeToken(token);
    // Pull live agent/business names if present.
    const { agentData, setAgentById, getAgentById } = useAgentStore();
    // Deep-link support: email / push CTAs land here as
    // /recommended-plan?agentId=<id>&mode=plans. Prefer the query agentId so
    // plan-assign targets the right agent, then fall back to sessionStorage.
    const queryAgentId = new URLSearchParams(location.search).get("agentId");
    const selectedAgentId =
        queryAgentId ||
        sessionStorage.getItem("SelectAgentId") ||
        sessionStorage.getItem("agentId") ||
        null;
    // Persist a deep-linked agentId so a refresh / other screens keep it.
    useEffect(() => {
        if (queryAgentId) {
            sessionStorage.setItem("SelectAgentId", queryAgentId);
            sessionStorage.setItem("agentId", queryAgentId);
        }
    }, [queryAgentId]);

    // Ownership guard — the agent shown here MUST belong to the logged-in user.
    // A nudge for User A's agent must NOT open for User B just because B is
    // signed in on the same device. We verify the agent's owner (agent.userId)
    // against the token's user id for ANY agentId in scope (deep-link query OR
    // sessionStorage), so a stale/cross-account agentId can never leak through.
    // Mismatch → clear the deep-link state and send them to their own home.
    const [ownershipOk, setOwnershipOk] = useState(!selectedAgentId);
    useEffect(() => {
        const myId = decodeTokenData?.id;
        // Team members operate on the owner's agents — allow the owner's id too.
        const myOwnerId = decodeTokenData?.ownerId;
        // Nothing to verify (no agent) or no identity yet → let it render; auth
        // is already gated by SecureRoute.
        if (!selectedAgentId) {
            setOwnershipOk(true);
            return;
        }
        if (!token || myId == null) {
            setOwnershipOk(false);
            return;
        }
        let cancelled = false;
        setOwnershipOk(false);
        const clearDeepLinkAndGoHome = () => {
            sessionStorage.removeItem("SelectAgentId");
            sessionStorage.removeItem("agentId");
            localStorage.removeItem("postLoginRedirect");
            navigate("/fast-agent-detail", { replace: true });
        };
        (async () => {
            try {
                const res = await fetchAgentById(selectedAgentId);
                const agent = res?.data?.data || res?.data || res || {};
                const ownerId =
                    agent?.userId ?? agent?.user_id ?? agent?.agent?.userId;
                const isMine =
                    String(ownerId) === String(myId) ||
                    (myOwnerId != null && String(ownerId) === String(myOwnerId));
                if (cancelled) return;
                if (ownerId == null || !isMine) {
                    // Not this user's agent → drop the deep-link so it can't
                    // re-fire, and bounce them to their own home.
                    clearDeepLinkAndGoHome();
                    return;
                }
                setOwnershipOk(true);
            } catch (err) {
                console.error("[RecommendedPlan] ownership check failed:", err?.message || err);
                if (!cancelled) clearDeepLinkAndGoHome();
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgentId, token]);
    // Cached agent (survives the zustand reset that a page refresh causes).
    const cachedAgent = getAgentById(selectedAgentId);
    // Pull live agent/business names — store first, then the multi-agent
    // cache, then a generic fallback. Without the cache step a refresh
    // (agentData=null) would show the literal "Sophia" / "your business".
    const agentName =
        agentData?.agent?.agentName ||
        cachedAgent?.agentData?.agent?.agentName ||
        "Sophia";
    const businessName =
        agentData?.business?.businessName ||
        agentData?.business?.googleBusinessName ||
        cachedAgent?.agentData?.business?.businessName ||
        cachedAgent?.agentData?.business?.googleBusinessName ||
        "your business";

    // Refresh resets the store — if we have an agentId but no agent data,
    // fetch it once and cache it so the name/business render correctly.
    useEffect(() => {
        if (!selectedAgentId) return;
        if (agentData?.agent?.agentName) return;
        if (cachedAgent?.agentData?.agent?.agentName) return;
        (async () => {
            try {
                const res = await fetchAgentById(selectedAgentId);
                const agent = res?.data || res || {};
                const business = agent?.business || null;
                setAgentById(selectedAgentId, { agent, business });
            } catch (err) {
                console.error("[RecommendedPlan] agent load failed:", err?.message || err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgentId]);

    // Pay-As-You-Go is a SEPARATE subscription — a PAYG agent's `agentPlan` is
    // usually still "free", so the plan key alone can't detect it. We check the
    // per-agent PAYG status explicitly. Start as `null` (= "checking") and let
    // the AUTHORITATIVE API decide — we deliberately DON'T seed from the shared
    // localStorage "isPayg" flag, which is stale-prone (a free agent could
    // carry a leftover `true` and wrongly skip the GoLivePayg screen).
    const [paygActive, setPaygActive] = useState(null);
    useEffect(() => {
        const customerId = decodeTokenData?.customerId;
        if (!selectedAgentId || !customerId || !token) {
            // Can't verify → fall back to the shared flag so we never hang.
            setPaygActive(localStorage.getItem("isPayg") === "true");
            return;
        }
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/pay-as-you-go-status-check`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ agentId: selectedAgentId, customerId }),
                });
                const data = await res.json();
                const active = !!data?.success; // true only when an ACTIVE PAYG sub exists
                setPaygActive(active);
                // Keep the shared flag in sync (other screens read it too).
                if (active) localStorage.setItem("isPayg", "true");
                else localStorage.removeItem("isPayg");
            } catch (err) {
                console.error("[RecommendedPlan] PAYG status check failed:", err?.message || err);
                setPaygActive(localStorage.getItem("isPayg") === "true");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAgentId]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const mode = params.get("mode");
        /*
         * from=upgrade — "Upgrade Agent" action ka marker (FastAgentDetails ka
         * handleUpgradeClick). Do kaam karta hai:
         *   1. Yahan seedha "Choose Your Plan" grid kholta hai — upgrade tap ke
         *      baad user ko plans hi dikhne chahiye, pitch screen nahi. Ye
         *      DONO branches pe laagu hai (paid plan upgrade aur variant B).
         *   2. GlobalCallBanner ise dekh kar purple call banner chhupa deta hai
         *      (ye live sales call nahi hai).
         * mode=plans jaisa tha waisa hi chalta hai — lifecycle emails aur
         * usage-milestone push abhi bhi usi se aate hain.
         */
        if (mode === "plans" || params.get("from") === "upgrade") {
            setView("plans");
            setPromoVisible(true);
        } else if (mode === "trial") {
            setView("trial");
            setPromoVisible(true);
        }
    }, [location.search]);

    useEffect(() => {
        if (!token || !selectedAgentId) return;
        axios
            .post(
                `${API_BASE}/onboarding/pricing/seen`,
                { agentId: selectedAgentId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .catch((err) => {
                console.error(
                    "Failed to mark pricing screen seen:",
                    err?.response?.data || err?.message
                );
            });
    }, [token, selectedAgentId]);

    // 1) Country → currency, same approach as /plan.
    //
    // TIMEOUT zaroori hai: ye ek THIRD-PARTY call hai aur products wala effect
    // (neeche) `if (!userCurrency) return` pe khada hai. Agar ye request kabhi
    // resolve/reject na ho — adblocker, corporate proxy, ya network stall —
    // to userCurrency khaali reh jaata, products fetch chalta hi nahi, aur
    // `loading` hamesha true rehne se user ko sirf grey skeleton cards dikhte
    // hain. Bina timeout ke axios anant kaal tak latka reh sakta hai.
    // 4s baad reject → catch → "usd" fallback → plans normally load ho jaate.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get("https://ipinfo.io/json", {
                    timeout: 4000,
                });
                if (cancelled) return;
                const country = res?.data?.country;
                setUserCurrency(COUNTRY_CURRENCY_MAP[country] || "usd");
                if (res?.data?.city) setUserCity(res.data.city);
            } catch {
                if (!cancelled) setUserCurrency("usd");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // 2) Fetch products. We don't flip `loading` back to true on
    //    currency-driven refetches (would briefly wipe rendered cards), and
    //    we don't filter prices by currency — we just unwrap currency_options
    //    when present, otherwise fall back to the base price. Together
    //    these prevent the "price flickers and disappears" bug.
    useEffect(() => {
        if (!userCurrency) return;
        let cancelled = false;
        setLoading((prev) => (products.length === 0 ? true : prev));

        fetch(`${API_BASE}/products`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        })
            .then((r) => r.json())
            .then((data) => {
                if (cancelled) return;
                if (!Array.isArray(data)) {
                    setLoading(false);
                    return;
                }

                const enriched = data
                    .map((product) => {
                        const matchedData = product.data?.find(
                            (p) => p.id === product.id
                        );

                        const enrichedPrices = (product.prices || []).map(
                            (price) => {
                                let displayPrice = price.unit_amount / 100;
                                let currency = price.currency;
                                const option = price.currency_options?.find(
                                    (opt) =>
                                        opt.currency ===
                                        userCurrency.toLowerCase()
                                );
                                if (option) {
                                    displayPrice = option.unit_amount / 100;
                                    currency = option.currency;
                                }
                                return {
                                    ...price,
                                    unit_amount: displayPrice * 100,
                                    currency: (currency || "usd").toLowerCase(),
                                };
                            }
                        );

                        return {
                            ...product,
                            prices: enrichedPrices,
                            metadata:
                                matchedData?.metadata || product.metadata || {},
                        };
                    })
                    .filter(
                        (p) =>
                            (p.name || "").trim().toLowerCase() in PLAN_DEFS
                    )
                    .sort((a, b) => {
                        const aKey = (a.name || "").trim().toLowerCase();
                        const bKey = (b.name || "").trim().toLowerCase();
                        return PLAN_DEFS[aKey].order - PLAN_DEFS[bKey].order;
                    });

                setProducts(enriched);
                setError("");
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                if (products.length === 0) setError("Failed to load plans.");
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCurrency, token]);

    // 3) 10s promo timer — runs only on the plans view, only once.
    useEffect(() => {
        if (view !== "plans") return undefined;
        if (promoVisible) return undefined;
        promoTimerRef.current = setTimeout(
            () => setPromoVisible(true),
            PROMO_DELAY_MS
        );
        return () => {
            if (promoTimerRef.current) clearTimeout(promoTimerRef.current);
        };
    }, [view, promoVisible]);

    // ---- helpers ---------------------------------------------------------

    const getPriceObj = (product, interval) =>
        product?.prices?.find((p) => p.interval === interval);

    const getMonthlyMinutes = (product) => {
        const monthlyPrice = getPriceObj(product, "month");
        return (
            monthlyPrice?.metadata?.["minutes-month"] ||
            product?.metadata?.["minutes-month"] ||
            null
        );
    };

    // Yearly toggle: still show "/month" but at the per-month rate the user
    // would pay if billed annually (yearly / 12).
    const getDisplayPrice = (product) => {
        const monthly = getPriceObj(product, "month");
        const yearly = getPriceObj(product, "year");
        if (billingInterval === "year" && yearly) {
            return {
                amount: yearly.unit_amount / 100 / 12,
                currency: yearly.currency,
            };
        }
        if (monthly) {
            return {
                amount: monthly.unit_amount / 100,
                currency: monthly.currency,
            };
        }
        return null;
    };

    const formatPrice = (amount) =>
        Number.isFinite(amount) ? Math.round(amount).toLocaleString() : "0";

    // Upgrade-flow detection — set by FastAgentDetails handleUpgradeClick.
    // When true: hide the trial pitch and disable current + downgrade cards.
    const upgradeReturnTo = useMemo(
        () => sessionStorage.getItem("upgradeReturnTo") || "",
        []
    );
    const isUpgradeFlow = !!upgradeReturnTo;
    // Guards the one-time history push in the back-guard effect below so it
    // doesn't stack extra entries when the effect re-runs (e.g. agentData load).
    const backGuardPushedRef = useRef(false);

    // GA4: plans screen shown. Kept with the other hooks (above all early
    // returns / handlers) so it never becomes a conditional hook call.
    useEffect(() => {
        trackEvent("view_plans", {
            entry: isUpgradeFlow ? "after_dashboard" : "primary",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /*
     * BACK = /fast-agent-detail. Bas itna.
     *
     * Is screen se peeche jaane ka matlab har haalat me ek hi hai — dashboard.
     * Plan free ho ya paid, variant A ho ya B, user $5 offer dekh raha ho ya
     * "Choose Your Plan" grid — koi farak nahi padta.
     *
     * KAISE: sirf popstate sun kar navigate karna KAAM NAHI KARTA — back dabte
     * hi React Router apna handler pehle chalata hai aur hamara navigate race
     * me haar jaata hai. Isliye mount pe ek DUPLICATE history entry
     * ("sentinel") push karte hain: back use khaata hai, URL nahi badalta,
     * Router ko koi change dikhta hi nahi, aur phir hum aaram se navigate kar
     * dete hain. Yahi pattern lib/useBackTo.js me hai jo FastSetup use karta
     * hai — wahan ke comments me iske do common failure modes likhe hain.
     *
     * Sentinel THEEK EK BAAR, mount pe. Deps khaali hain to effect dobara
     * chalta hi nahi — pehle `agentData` dep me tha, jiske load hone par effect
     * re-run hota, cleanup listener hata deta, aur us waqt shart false nikal
     * jaati to naya listener lagta hi nahi. Back chup-chaap mar jaata tha.
     */
    useEffect(() => {
        // Back guard runs for EVERY visit to this screen: back from
        // /recommended-plan must always land on the dashboard, whether the user
        // is upgrading, on the app surface, or on plain web. (An earlier version
        // gated this on isUpgradeFlow || app-surface, which left back doing
        // nothing for the remaining cases.)
        //
        // Seed an extra history entry so the first back press has something to
        // consume — without this, on some browsers the popstate fires once but
        // the URL has already changed past our control. Push ONCE even if the
        // effect re-runs (agentData load).
        if (!backGuardPushedRef.current) {
            backGuardPushedRef.current = true;
            window.history.pushState(
                { rpBackGuard: true },
                "",
                window.location.href,
            );
        }

        const handleBack = () => {
            // Upgrade session keys saaf karo, warna agli visit stale state
            // inherit kar leti hai. (upgradeReturnTo Stripe ka SUCCESS url hai,
            // back target nahi — do cheezein pehle conflate ho gayi thi aur back
            // user ko setup wizard me phenk deta tha.)
            sessionStorage.removeItem("upgradeReturnTo");
            sessionStorage.removeItem("upgradeOldSubId");
            sessionStorage.removeItem("currentAgentPlan");
            sessionStorage.removeItem("updateBtn");
            navigate("/fast-agent-detail", { replace: true });
        };

        window.addEventListener("popstate", handleBack);
        return () => window.removeEventListener("popstate", handleBack);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Current agent plan key (lowercase, trimmed) — used to gate current
    // and lower-tier cards during an upgrade.
    //
    // Fallback to sessionStorage("currentAgentPlan"): when the user goes
    // Stripe checkout and CANCELS, the cancel-payment redirect causes a
    // full page reload, which resets the zustand store (agentData=null).
    // Without this fallback all cards would unlock on return and a Scaler
    // user could "downgrade" to Starter on the second attempt.
    // Keep this separate from selectedPlan: selectedPlan is overwritten
    // with the plan the user is trying to buy, while currentAgentPlan must
    // continue to represent the agent's real plan until payment succeeds.
    const currentPlanKey = useMemo(
        () => {
            const fromAgent = (agentData?.agent?.agentPlan || "")
                .trim()
                .toLowerCase();
            if (fromAgent) return fromAgent;
            const fromSession = (
                sessionStorage.getItem("currentAgentPlan") || ""
            )
                .trim()
                .toLowerCase();
            return fromSession;
        },
        [agentData?.agent?.agentPlan]
    );
    const currentPlanOrder = useMemo(() => {
        const def = PLAN_DEFS[currentPlanKey];
        return Number.isFinite(def?.order) ? def.order : -1;
    }, [currentPlanKey]);

    const cards = useMemo(
        () =>
            products.map((product) => {
                const key = (product.name || "").trim().toLowerCase();
                const def = PLAN_DEFS[key] || {};
                const cardOrder = Number.isFinite(def.order) ? def.order : -1;
                // During an upgrade flow: lock the current plan AND any
                // lower-tier plan. Outside upgrade flow these flags stay
                // false and the cards behave as before.
                const isCurrentPlan = isUpgradeFlow && key === currentPlanKey;
                const isDowngrade =
                    isUpgradeFlow &&
                    currentPlanOrder >= 0 &&
                    cardOrder >= 0 &&
                    cardOrder < currentPlanOrder;
                return {
                    id: key,
                    name: product.name,
                    icon: def.icon,
                    summary: def.summary,
                    recommended: !!def.recommended,
                    minutes: getMonthlyMinutes(product),
                    display: getDisplayPrice(product),
                    raw: product,
                    isCurrentPlan,
                    isDowngrade,
                    isLocked: isCurrentPlan || isDowngrade,
                };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [products, billingInterval, isUpgradeFlow, currentPlanKey, currentPlanOrder]
    );

    /*
     * Top-tier guard.
     *
     * If the user is already on the highest available plan (Growth, in
     * the current 3-tier matrix) and they hit /recommended-plan via
     * the upgrade flow, EVERY card ends up locked — every other card
     * is either their current plan or a downgrade. Without this gate
     * they'd land on a screen full of greyed-out cards with no clear
     * action. We pop a one-shot alert + bounce them back to wherever
     * they came from (upgradeReturnTo, falling back to /fast-agent-detail).
     */
    /*
     * Top-tier modal state. Replaces the previous window.alert which
     * looked out of place against the rest of the app's UI — the
     * native browser alert chrome doesn't match our purple/white
     * dialog language. Flip showTopTierModal true and the rendered
     * inline modal (below) handles the OK click → bounce-back nav.
     */
    const topTierAlertedRef = useRef(false);
    const [showTopTierModal, setShowTopTierModal] = useState(false);
    useEffect(() => {
        if (!isUpgradeFlow || cards.length === 0) return;
        if (topTierAlertedRef.current) return;
        // All meaningful cards locked means the user is already at the
        // top. We exclude the trial card from this check — it lives in
        // a separate UI surface and isn't part of the regular tier
        // ladder.
        const ladder = cards.filter((c) => c.id !== TRIAL_PLAN_NAME);
        if (ladder.length === 0) return;
        const everyLocked = ladder.every((c) => c.isLocked);
        if (!everyLocked) return;
        topTierAlertedRef.current = true;
        setShowTopTierModal(true);
    }, [isUpgradeFlow, cards]);

    const handleTopTierModalClose = () => {
        setShowTopTierModal(false);
        sessionStorage.removeItem("upgradeReturnTo");
        sessionStorage.removeItem("upgradeOldSubId");
        sessionStorage.removeItem("currentAgentPlan");
        sessionStorage.removeItem("updateBtn");
        const back = "/fast-agent-detail";
        navigate(back, { replace: true });
    };

    // For an upgrade flow, default-select the cheapest valid upgrade card
    // (so the pre-selected scaler doesn't leave a Scaler user stuck).
    useEffect(() => {
        if (!isUpgradeFlow || cards.length === 0) return;
        const currentSelected = cards.find((c) => c.id === selectedPlanId);
        if (currentSelected && !currentSelected.isLocked) return;
        const firstUpgrade = cards.find((c) => !c.isLocked);
        if (firstUpgrade) setSelectedPlanId(firstUpgrade.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUpgradeFlow, cards]);

    // On an upgrade flow, jump straight to the plans grid (skip the
    // "Here is what {agent} can do" pitch — irrelevant for paid users).
    useEffect(() => {
        if (isUpgradeFlow && view !== "plans") setView("plans");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUpgradeFlow]);

    const selectedCard = useMemo(
        () =>
            cards.find((c) => c.id === selectedPlanId) ||
            cards.find((c) => c.recommended) ||
            cards[0] ||
            null,
        [cards, selectedPlanId]
    );

    // The trial product — the trial flow needs its priceId regardless of
    // which card the user has highlighted.
    const trialCard = useMemo(
        () => cards.find((c) => c.id === TRIAL_PLAN_NAME) || null,
        [cards]
    );
    // n
    // ── Native app (Android OR iOS) + FREE agent: show the "Go live for
    //    $5/mo" screen instead of the Recommended Plan UI. If the agent
    //    already has a paid plan, the normal Recommended Plan screen runs as
    //    usual (even inside the WebView). Web always keeps the existing flow.
    const platform = detectPlatform();
    // Unified flow: use the app-surface gate (now true on web too) so the mobile
    // trial/$5 screens (GoLiveVariant) also show on web when the agent asks for
    // them — matching the plans popup. (Was: platform === android || ios.)
    const isNativeApp = isMobileAppSurface();
    // Decide by the agent's plan. We show the normal Recommended Plan UI ONLY
    // when the agent is on a KNOWN PAID plan. Everything else (free / demo /
    // empty / not-yet-loaded) gets the "Go live for $5/mo" screen.
    //
    // Why "known paid" instead of "is free": on a page refresh the zustand
    // store resets (agentData=null) and the plan can momentarily be an empty
    // string (FastAgentDetails persists `agentPlan || ""`, so a free agent has
    // ""). Checking `=== "free"` then failed and flashed the web plans screen
    // inside the app. Treating empty/unknown as "not paid" keeps the app on
    // GoLivePayg until we positively know it's a paid agent.
    const isPaidAgent = ["starter", "scaler", "growth", "payg", "pay-as-you-go"].includes(currentPlanKey);
    // Lifecycle "See your plan" / "Reactivate" deep-links (freeMinutes80, churned)
    // pass ?mode=plans and WANT the plans list even for a non-paid agent — honour
    // it so those nudges don't land on GoLivePayg inside the WebView. The normal
    // app flow (no mode=plans) still shows GoLivePayg for non-paid agents.
    const wantsPlansView = new URLSearchParams(location.search).get("mode") === "plans";
    // During an UPGRADE (user tapped "Upgrade Plan" — e.g. a $5 PAYG or $20
    // trial agent) ALWAYS show the plans list (Starter/Scaler/Growth), never the
    // GoLiveVariant $5 screen — even if the plan key hasn't loaded yet.
    //
    // isUpgradeFlow (sessionStorage upgradeReturnTo) akela kaafi nahi hai:
    // variant B ka upgrade rasta wo key set nahi karta, aur bina is check ke
    // free agent yahin GoLiveVariant pe atak jaata — jabki "Upgrade Agent"
    // dabane par plans list hi dikhni chahiye. URL marker dono raston ko cover
    // karta hai aur refresh ke baad bhi bacha rehta hai.
    const cameFromUpgrade =
        new URLSearchParams(location.search).get("from") === "upgrade";
    // India (.in) always shows the PLANS list (Starter / Scaler / Growth), never
    // the $20 trial / $5 PAYG screen. Those two offers are priced in dollars and
    // are part of the US go-live experiment; India sells its own plans, and
    // isMobileAppSurface() is now true on web too, so without this every free
    // India agent landed on the trial screen instead of the plans it came for.
    //
    // An AppSumo buyer is excluded even on .in: their lifetime deal is already
    // paid and their minutes sit in the credit pools, so the plans list has
    // nothing to sell them. They keep the existing screen, same as on .ai.
    const indiaSite = isIndiaSite() && !isAppSumoUser();
    if (!indiaSite && isNativeApp && !isPaidAgent && !isUpgradeFlow && !cameFromUpgrade && !wantsPlansView) {
        // iOS → always the 21-day trial; Android → alternate $5/mo vs trial by
        // visit count. GoLiveVariant owns that decision (see its comments).
        return <GoLiveVariant />;
    }




    // ---- handlers --------------------------------------------------------
    // updates  m
    const goToTrial = () => {
        setView("trial");
        // Reset promo state so coming back lands on plans cleanly.
        setPromoVisible(true);
    };

    // Back out of Choose Your Plan. Mirrors the popstate upgrade back-handler
    // above: return where the user came from (validated by safeBackPath) and
    // clear the upgrade session keys so a later visit doesn't inherit stale
    // state.
    const handleGoBack = () => {
        sessionStorage.removeItem("upgradeReturnTo");
        sessionStorage.removeItem("upgradeOldSubId");
        sessionStorage.removeItem("currentAgentPlan");
        sessionStorage.removeItem("updateBtn");
        navigate(resolveBackPath(location?.state?.locationPath), { replace: true });
    };

    /*
     * When to show the button.
     *
     * It used to be gated on `isNativeApp` alone, with the reasoning "web
     * already has browser back". That misses two surfaces:
     *   - an INSTALLED PWA runs standalone with no browser chrome, so there is
     *     no back button on screen at all;
     *   - the upgrade entry (FastAgentDetails → Upgrade) is an in-app jump the
     *     user must be able to cancel, and browser back is unreliable here
     *     because several screens in this app install popstate blockers.
     * Still hidden for the first-run onboarding funnel (FastSetup navigates
     * here with `replace: true` and no locationPath): there is no previous
     * screen to return to, and picking a plan is a required step.
     */
    const isStandalonePWA =
        typeof window !== "undefined" &&
        (window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
            window.navigator?.standalone === true);
    const showBackBtn =
        isNativeApp ||
        isStandalonePWA ||
        isUpgradeFlow ||
        !!location?.state?.locationPath;

    const revealPlanFeatures = (card) => {
        if (!card || card.isLocked) return;
        // Usi card par dobara tap = band karo (arrow wapas neeche ghoomta hai).
        if (showComparison && selectedPlanId === card.id) {
            setShowComparison(false);
            return;
        }
        setError("");
        setSelectedPlanId(card.id);
        setShowComparison(true);
        window.requestAnimationFrame(() => {
            comparisonRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    };
    // Continue (regular checkout) → /create-checkout-session, success URL
    // routes the user past Thankyou straight to /go-live-setup which fires
    // the agent-upgrade call on mount. See GoLiveWizard.
    // Upgrade flows still use the normal /go-live-setup success path; the
    // wizard handles the post-checkout update/cancel bookkeeping.
    const handleContinue = async () => {
        if (checkoutLoading) return;
        // Guard: agent was JUST paid (back-navigated here) → don't double-charge.
        if (isAgentRecentlyPaid(sessionStorage.getItem("SelectAgentId"))) {
            navigate("/fast-agent-detail", { replace: true });
            return;
        }
        if (!selectedCard) return;
        if (selectedCard.isLocked) {
            setError(
                selectedCard.isCurrentPlan
                    ? "This is your current plan."
                    : "Downgrades aren't supported here. Please contact support@rxpt.us."
            );
            return;
        }


        const customerId = decodeTokenData?.customerId;
        // Team members act on an account OWNER's behalf. Multi-owner: attribute
        // the subscription to the SELECTED agent's owner (set on card-click),
        // falling back to the legacy single teamOwnerId, then own id. The
        // backend additionally forces the owner's Stripe customer + userId
        // server-side by looking up the agent, so the actual charge can't be
        // misrouted even if this value were wrong.
        const selectedOwnerId = typeof window !== "undefined" ? sessionStorage.getItem("SelectAgentOwnerId") : null;
        const teamOwnerId = typeof window !== "undefined" ? localStorage.getItem("teamOwnerId") : null;
        const userId = selectedOwnerId || teamOwnerId || decodeTokenData?.id;
        if (!token || !customerId || !userId) {
            setError("Missing account info. Please refresh and sign in again.");
            return;
        }

        const priceObj = selectedCard.raw?.prices?.find(
            (p) => p.interval === billingInterval
        );
        if (!priceObj?.id) {
            setError(
                `${selectedCard.name} isn't available for ${billingInterval === "year" ? "yearly" : "monthly"
                } billing right now.`
            );
            return;
        }

        const agentId =
            sessionStorage.getItem("SelectAgentId") ||
            sessionStorage.getItem("agentId") ||
            agentData?.agent?.agent_id ||
            null;
        sessionStorage.setItem("selectedPlan", selectedCard.id);
        sessionStorage.setItem("selectedBillingInterval", billingInterval);
        const currentCancelPath =
            window.location.pathname + (window.location.search || "?from=upgrade");
        sessionStorage.setItem("cancelReturnTo", currentCancelPath);
        setPaymentCancelReturn(currentCancelPath);

        // Upgrade flow: keep the normal post-payment path, while preserving
        // ?upgraded=1 so GoLiveWizard can cancel any old subscription.
        const successParams = new URLSearchParams({ fromCheckout: "1" });
        if (agentId) successParams.append("agentId", agentId);
        if (userId) successParams.append("userId", userId);
        if (isUpgradeFlow) successParams.append("upgraded", "1");
        const successPath = isUpgradeFlow ? upgradeReturnTo : "/go-live-setup";
        /*
         * "Thank You" / Setup Progress screen SIRF $5 PAYG purchase ke liye hai
         * (GoLivePayg ka quick-setup-payg flow).
         *
         * Pehle ye WebView shell me har plan purchase par dikhti thi, isliye
         * MOBILE par Scaler / Starter / Growth par bhi thank-you page aa jata
         * tha jabki WEB par seedha destination khulta tha. Ab dono surfaces
         * ek jaise — seedha asli destination.
         */
        const successUrl = `${window.location.origin}${successPath}?${successParams.toString()}`;
        const cancelUrl = `${window.location.origin}/cancel-payment`;

        // Embedded renders Stripe INSIDE this page (CheckoutSheet) instead of
        // navigating to checkout.stripe.com — which is what used to destroy the
        // React tree and with it the live sales call.
        const embedded = isEmbeddedCheckoutEnabled();

        try {
            setCheckoutLoading(true);
            setError("");
            const res = await axios.post(
                `${API_BASE}${embedded ? "/create-checkout-session-embedded" : "/create-checkout-session"}`,
                {
                    customerId,
                    priceId: priceObj.id,
                    userId,
                    // Embedded sessions carry no success/cancel url of their own,
                    // but the backend still needs these to mint the HOSTED
                    // fallback session it returns alongside the client secret.
                    url: successUrl,
                    cancelUrl,
                    currency: (priceObj.currency || userCurrency || "usd")
                        .toLowerCase(),
                    agentId,
                    // Deliberate plan change. The backend's duplicate-payment
                    // guard (checkoutSession → hasActiveSubscription) refuses a
                    // new checkout with 409 duplicate_subscription whenever the
                    // agent already has a LIVE Stripe sub — that's the back-nav
                    // double-charge net. A real upgrade MUST pass through it:
                    // the previous plan is retired right after checkout by
                    // cancelSupersededSubscription. Without this flag every paid
                    // agent's "Upgrade Plan" 409'd and the user just saw
                    // "Could not start checkout." It also doubles as the marker
                    // that webhook gates on.
                    isUpgrade: isUpgradeFlow || cameFromUpgrade,
                    // Hint at the subscription this purchase replaces, stamped
                    // onto the new subscription's metadata. Only a hint: it comes
                    // from agent.subscriptionId, which the assign-number flow
                    // overwrites with the add-on subscription id, so the webhook
                    // resolves the real target by product and falls back to this
                    // only for subs predating agentId metadata.
                    oldSubscriptionId:
                        sessionStorage.getItem("upgradeOldSubId") || undefined,
                    // We fire GA4 `purchase` client-side on the thank-you
                    // landing → tell the backend to skip the server-side
                    // first-purchase for this subscription (no double count).
                    clientFiresPurchase: true,
                    isUpgrade: true,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const purchaseValue =
                priceObj.unit_amount != null ? priceObj.unit_amount / 100 : undefined;
            const purchaseCurrency = (priceObj.currency || userCurrency || "usd").toUpperCase();
            const purchaseBillingCycle =
                billingInterval === "year" ? "yearly" : "monthly";

            if (embedded && res?.data?.clientSecret) {
                // GA4: same purchase either way, so the same events fire here.
                trackEvent("begin_checkout", {
                    plan: selectedCard.name,
                    billing_cycle: purchaseBillingCycle,
                    value: purchaseValue,
                    currency: purchaseCurrency,
                });
                stashPendingPurchase({
                    transaction_id: `${userId || "uid"}-${Date.now()}`,
                    plan: selectedCard.name,
                    billing_cycle: purchaseBillingCycle,
                    value: purchaseValue,
                    currency: purchaseCurrency,
                });
                useCheckoutStore.getState().openCheckout({
                    clientSecret: res.data.clientSecret,
                    sessionId: res.data.sessionId,
                    hostedUrl: res.data.checkoutUrl || null,
                    meta: {
                        agentId,
                        userId,
                        planName: selectedCard.name,
                        value: purchaseValue,
                        currency: purchaseCurrency,
                        // Where success_url pointed. No /payment-success detour:
                        // that interstitial only existed to catch the WebView
                        // coming back from a full-page redirect, and nothing
                        // leaves the page now.
                        next: successPath,
                        extraParams: isUpgradeFlow ? { upgraded: "1" } : {},
                    },
                });
                // The sheet owns the screen now; drop the button's spinner so a
                // cancel doesn't leave it stuck on "please wait".
                setCheckoutLoading(false);
                return;
            }

            if (res?.data?.checkoutUrl) {
                // GA4: Stripe checkout opening — last client event in the money flow.
                trackEvent("begin_checkout", {
                    plan: selectedCard.name,
                    billing_cycle: purchaseBillingCycle,
                    value: purchaseValue,
                    currency: purchaseCurrency,
                });
                // Stash the deal so the thank-you landing (PaymentSuccess /
                // GoLiveWizard, reached with fromCheckout=1) can fire the
                // client-side `purchase` for this FIRST payment. Renewals are
                // handled server-side. transaction_id is a client token — the
                // server no longer fires purchase for the first invoice, so
                // there's nothing to dedupe against.
                stashPendingPurchase({
                    transaction_id: `${userId || "uid"}-${Date.now()}`,
                    plan: selectedCard.name,
                    billing_cycle: purchaseBillingCycle,
                    value: purchaseValue,
                    currency: purchaseCurrency,
                });
                try { sessionStorage.removeItem("globalCallBannerEnded"); } catch (_e) { }
                window.location.href = res.data.checkoutUrl;
                return;
            }
            setError("Could not start checkout. Please try again.");
        } catch (err) {
            console.error(
                "Checkout error:",
                err?.response?.data || err?.message
            );
            // The duplicate-subscription refusal (409) carries an actionable
            // message — show it instead of the generic retry line, which reads
            // like a transient failure and sends the user in circles.
            const apiMessage =
                err?.response?.status === 409
                    ? err?.response?.data?.message
                    : null;
            setError(apiMessage || "Could not start checkout. Please try again.");
        } finally {
            setCheckoutLoading(false);
        }
    };

    // 21-day Starter trial Continue → /freeSetup-plan ($20 activation +
    // 21-day Stripe trial, fixed at 150 trial minutes).
    const handleStartTrial = async () => {
        if (trialLoading) return;
        if (isAgentRecentlyPaid(sessionStorage.getItem("SelectAgentId"))) {
            navigate("/fast-agent-detail", { replace: true });
            return;
        }
        if (!trialCard) {
            setError("Trial isn't available right now — please try again.");
            return;
        }

        const userId = decodeTokenData?.id;
        const email = decodeTokenData?.email;
        if (!token || !userId || !email) {
            setError("Missing account info. Please refresh and sign in again.");
            return;
        }

        // Backend expects the MONTHLY priceId for the plan being trialled —
        // Stripe applies trial_period_days to that subscription.
        const monthlyPriceId = trialCard.raw?.prices?.find(
            (p) => p.interval === "month"
        )?.id;
        if (!monthlyPriceId) {
            setError(`${TRIAL_PLAN_NAME} monthly price not configured.`);
            return;
        }

        const agentId =
            sessionStorage.getItem("SelectAgentId") ||
            sessionStorage.getItem("agentId") ||
            agentData?.agent?.agent_id ||
            null;
        sessionStorage.setItem("selectedPlan", TRIAL_PLAN_NAME);
        sessionStorage.setItem("selectedBillingInterval", "month");

        // After the $20 charge, Stripe redirects to /go-live-setup so the
        // wizard takes over (same destination as the regular checkout
        // flow). fromCheckout=1 trips the post-checkout upgrade hook there.
        const currentCancelPath =
            window.location.pathname + (window.location.search || "?from=upgrade");
        sessionStorage.setItem("cancelReturnTo", currentCancelPath);
        setPaymentCancelReturn(currentCancelPath);
        const successParams = new URLSearchParams({
            fromCheckout: "1",
            trial: "1",
        });
        if (agentId) successParams.append("agentId", agentId);
        if (userId) successParams.append("userId", userId);
        const successUrl = `${window.location.origin}/go-live-setup?${successParams.toString()}`;
        const cancelUrl = `${window.location.origin}/cancel-payment`;

        // Plan price label for the activation email — best-effort, falls
        // back to a sensible default if the monthly price isn't on hand.
        const monthlyAmount = trialCard.raw?.prices?.find(
            (p) => p.interval === "month"
        )?.unit_amount;
        const planPriceLabel =
            Number.isFinite(monthlyAmount)
                ? `$${Math.round(monthlyAmount / 100)}/month`
                : undefined;

        const embeddedTrial = isEmbeddedCheckoutEnabled();

        try {
            setTrialLoading(true);
            setError("");
            const res = await axios.post(
                `${API_BASE}${embeddedTrial ? "/freeSetup-plan-embedded" : "/freeSetup-plan"}`,
                {
                    email,
                    priceId: monthlyPriceId,
                    // Only used to mint the hosted fallback session on the
                    // embedded endpoint — the embedded session itself never
                    // redirects and cannot accept a cancel url at all.
                    sucessurl: successUrl,
                    cancelurl: cancelUrl,
                    userId,
                    planName: TRIAL_PLAN_NAME,
                    agentId,
                    business_name: businessName,
                    activation_price: TRIAL_ACTIVATION_PRICE_LABEL,
                    trial_days: TRIAL_DAYS,
                    trial_minutes: TRIAL_MINUTES,
                    plan_price: planPriceLabel,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (embeddedTrial && res?.data?.clientSecret) {
                useCheckoutStore.getState().openCheckout({
                    clientSecret: res.data.clientSecret,
                    sessionId: res.data.sessionId,
                    hostedUrl: res.data.checkoutUrl || null,
                    meta: {
                        agentId,
                        userId,
                        planName: TRIAL_PLAN_NAME,
                        next: "/go-live-setup",
                        // Kept from the old success_url — GoLiveWizard branches
                        // on ?trial=1.
                        extraParams: { trial: "1" },
                    },
                });
                setTrialLoading(false);
                return;
            }

            if (res?.data?.checkoutUrl) {
                try { sessionStorage.removeItem("globalCallBannerEnded"); } catch (_e) { }
                window.location.href = res.data.checkoutUrl;
                return;
            }
            setError("Could not start the 21-day trial. Please try again.");
        } catch (err) {
            console.error(
                "Trial checkout error:",
                err?.response?.data || err?.message
            );
            setError("Could not start the 21-day trial. Please try again.");
        } finally {
            setTrialLoading(false);
        }
    };

    // ---- render: trial view ---------------------------------------------

    if (view === "trial") {
        return (
            <>

                <div className={styles.screen}>
                    <div className={styles.capWrap}>
                        <h1 className={styles.capTitle}>
                            Here is what
                            <br />
                            <span className={styles.capTitleHighlight}>
                                {agentName}
                            </span>{" "}
                            can do
                            <br />
                            for{" "}
                            <span className={styles.capTitleUnderline}>
                                {businessName}
                            </span>
                        </h1>

                        <div className={styles.compareCard}>
                            <div className={styles.compareIcon}>
                                <img
                                    src="/svg/human_receptionist.svg"
                                    alt="human_receptionist"
                                />
                            </div>
                            <div className={styles.compareContent}>
                                <p className={styles.compareTop}>
                                    Human receptionist:{" "}
                                    <strong>$105/day</strong>
                                </p>
                                <p className={styles.compareSub}>($3,500/month)</p>
                                <div className={styles.compareDivider} />
                                <p className={styles.compareBottom}>
                                    <span className={styles.compareName}>
                                        {agentName}
                                    </span>{" "}
                                    starts at{" "}
                                    <span className={styles.compareCheap}>
                                        $4/day
                                    </span>
                                </p>
                            </div>
                        </div>

                        <div className={styles.capList}>
                            {CAPABILITIES.map((cap) => (
                                <div className={styles.capCard} key={cap.title}>
                                    <div className={styles.capCardIcon}>
                                        <img src={cap.icon} alt={cap.title} />
                                        <span className={styles.capCheck}>✓</span>
                                    </div>
                                    <div className={styles.capCardBody}>
                                        <h4 className={styles.capCardTitle}>
                                            {cap.title}
                                        </h4>
                                        <p className={styles.capCardDesc}>
                                            {cap.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>


                </div>
                <div className={styles.capFooter}>
                    {/* <div className={styles.tagPill}>
                        <span className={styles.tagDot}></span>
                        All-in-one
                        <span className={styles.tagDot}></span>
                        Always on
                        <span className={styles.tagDot}></span>
                        Built for your business
                    </div> */}
                    <div className={styles.trustLine}>
                        Proven Architecture <span>|</span> No Contract{" "}
                        <span>|</span>{" "}
                        <span className={styles.stripeText}>
                            <span className={styles.stripeWord}>
                                <img src="/svg/stripe_logo.svg" alt="Stripe" /> Verified
                            </span>

                        </span>
                        {/* <div>
                            <p className={styles.trustTitle}>Bank-level</p>
                            <p className={styles.trustSub}>Security</p>
                        </div> */}
                    </div>
                    <div className={styles.trustItem}>
                        <span
                            className={`${styles.trustBadge} ${styles.trustBadgePurple}`}
                        >
                            $
                        </span>
                        <div>
                            <p className={styles.trustTitle}>No hidden fees</p>
                            <p className={styles.trustSub}>
                                Transparent policy
                            </p>
                        </div>
                    </div>
                </div>

                <div className={styles.continueBar}>
                    <button
                        type="button"
                        className={styles.continueBtn}
                        onClick={() => setView("plans")}
                    >
                        <span className={styles.continueAvatar}>
                            <img
                                src="/svg/receptionist_profile.png"
                                alt={agentName}
                            />
                        </span>
                        <span className={styles.ctaTextBlock}>
                            <span className={styles.ctaTitle}>
                                Choose Your Plan{" "}
                                <span className={styles.ctaArrow}>→</span>
                            </span>
                            <span className={styles.ctaSub}>
                                <span className={styles.ctaLock}><img src="/svg/cta_lock.svg" alt='cta_lock' /> 100% Secure.</span>
                                <span className={styles.ctaCheckMark}>✓ <span> Cancel anytime.</span></span>
                            </span>
                        </span>
                    </button>
                    <p className={styles.onboardedLine}>
                        <span className={styles.onboardedAvatars} aria-hidden="true">
                            <span className={`${styles.onboardedAvatar} ${styles.onboardedAvatarA}`}>AK</span>
                            <span className={`${styles.onboardedAvatar} ${styles.onboardedAvatarB}`}>PR</span>
                            <span className={`${styles.onboardedAvatar} ${styles.onboardedAvatarC}`}>MJ</span>
                        </span>
                        <span>
                            250+ businesses onboarded in{" "}
                            <strong>{userCity || "your area"}</strong>
                        </span>
                    </p>
                </div>

            </>
        );
    }

    // ---- render: plans view ---------------------------------------------

    return (
        <>


            <PlanSelectionView
                showBackBtn={showBackBtn}
                handleGoBack={handleGoBack}
                isUpgradeFlow={isUpgradeFlow}
                setTrialModalOpen={setTrialModalOpen}
                billingInterval={billingInterval}
                setBillingInterval={setBillingInterval}
                loading={loading}
                error={error}
                cards={cards}
                selectedPlanId={selectedPlanId}
                getCurrencySymbol={getCurrencySymbol}
                setError={setError}
                setSelectedPlanId={setSelectedPlanId}
                formatPrice={formatPrice}
                revealPlanFeatures={revealPlanFeatures}
                showComparison={showComparison}
                comparisonRef={comparisonRef}
                selectedCard={selectedCard}
                COMPARISON_ROWS={COMPARISON_ROWS}
                COMPARISON_COLUMNS={COMPARISON_COLUMNS}
                handleContinue={handleContinue}
                checkoutLoading={checkoutLoading}
                agentName={agentName}
            />

            <NewModal
                isOpen={trialModalOpen}
                onClose={() => setTrialModalOpen(false)}
                // India is not being asked to commit to anything here — the
                // minutes are already free — so the US framing would read as a
                // sales objection to an offer that has no price.
                title={indiaSite ? "Not ready to choose a plan yet?" : "Not Ready to Commit?"}
                height="auto"
                maxHeight="70vh"
                bodyMaxHeight="70vh"
            >
                <div className={styles.wrap}>
                    {/*
                      India (.in) gets a different offer here. The $20 / 21-day
                      trial is a dollar-priced US offer bought through Stripe;
                      India's own free allowance is 50 minutes, which the account
                      already has, so there is nothing to purchase. The modal
                      therefore points at trying the agent rather than at
                      checkout, and its CTA goes straight to the dashboard
                      instead of opening a Stripe session.

                      AppSumo buyers are excluded for the same reason they are
                      excluded from the plans screen itself: their minutes come
                      from the lifetime deal's credit pools, not this allowance.
                    */}
                    {indiaSite ? (
                        <>
                            <p className={styles.heading}>
                                No problem. You still have
                                <br /><span className={styles.headingAccent}>50 free minutes</span>
                                <br />to test your AI receptionist before upgrading.
                            </p>
                            {/*
                              Web-call testing only. The free allowance does NOT
                              include a VoIP number, call forwarding or the
                              contact-routing features — those arrive with a paid
                              plan, so the US trial's feature list would promise
                              what these minutes cannot deliver.
                            */}
                            <div className={styles.freeMinsPill}>
                                <span className={styles.freeMinsPillIcon} aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 7v5l3 2" />
                                    </svg>
                                </span>
                                <span>
                                    <span className={styles.minutesAccent}>50 free minutes</span>
                                    {" "}to explore, no card needed.
                                </span>
                            </div>
                            <p className={styles.freeMinsNote}>
                                You can continue to your dashboard for now, or go back
                                and choose a plan to activate your agent.
                            </p>
                            {/*
                              Primary action goes BACK to the plans behind this
                              modal — the user opened it from the plans screen, so
                              closing it returns them where they were. The
                              dashboard is the secondary, "not now" path.
                            */}
                            <AnimatedButton
                                label="Back to Checkout"
                                positionType="relative"
                                bottom={0}
                                width="100%"
                                onClick={() => {
                                    trackEvent("free_minutes_back_to_checkout", { from: "trial_offer" });
                                    setTrialModalOpen(false);
                                }}
                            />
                            <button
                                type="button"
                                className={styles.maybeLater}
                                onClick={() => {
                                    trackEvent("plans_dismissed", { from: "trial_offer" });
                                    navigate("/fast-agent-detail", { replace: true });
                                }}
                            >
                                Go to Dashboard
                            </button>
                        </>
                    ) : (
                        <>
                            <p className={styles.heading}>
                                Start <span className={styles.headingAccent}>21-Day Trial for $20</span>
                                <br />with <span className={styles.headingAccent}>Starter</span> Benefits
                            </p>
                            <p className={styles.subText}>
                                Experience the full power of Starter risk-free.<br />
                                Real calls | Real results | No commitment.
                            </p>
                            <div className={styles.includesBox}>
                                <p className={styles.includesTitle}>Trial Includes:</p>
                                {[
                                    { text: "Business phone number (VoIP) assigned" },
                                    { text: "Call forwarding & triage rules" },
                                    { text: "Contact sync & whitelist" },
                                    { text: "Automatic SPAM Block and Blacklist creation" },
                                    { text: null, jsx: <><span className={styles.minutesAccent}>150 minutes</span> for live call answering</> },
                                ].map((item, i) => (
                                    <div key={i} className={styles.includeItem}>
                                        <span className={styles.tick}><img src="/svg/green-check.svg" alt="green-check" /></span>
                                        <span>{item.jsx || item.text}</span>
                                    </div>
                                ))}
                            </div>
                            <AnimatedButton
                                label="Start TRIAL now"
                                isLoading={trialLoading}
                                positionType="relative"
                                bottom={0}
                                width="100%"
                                disabled={trialLoading || !trialCard}
                                onClick={handleStartTrial}
                            />
                            <button
                                type="button"
                                className={styles.maybeLater}
                                onClick={() => {
                                    trackEvent("plans_dismissed", { from: "trial_offer" });
                                    const back = "/fast-agent-detail";
                                    navigate(back, { replace: true })
                                }}
                            >
                                Maybe later
                            </button>
                        </>
                    )}
                </div>
            </NewModal>

            {/*
              Top-tier modal — replaces the previous window.alert.
              Matches the rest of the app's purple/white dialog
              language so the upgrade flow doesn't suddenly drop into
              native browser chrome. One CTA ("Got it") which calls
              handleTopTierModalClose → navigates back to wherever
              the user came from (upgradeReturnTo / dashboard).
              Backdrop click is a no-op so the user has to acknowledge
              the message before leaving the page.
            */}
            {showTopTierModal && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="top-tier-title"
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(15, 23, 42, 0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2000,
                        padding: 16,
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            maxWidth: 420,
                            background: "#fff",
                            borderRadius: 20,
                            padding: "24px 22px 20px",
                            boxShadow: "0 16px 48px rgba(15, 23, 42, 0.18)",
                            textAlign: "center",
                        }}
                    >
                        <div
                            aria-hidden="true"
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: "50%",
                                background: "#F3EEFF",
                                color: "#6524EB",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: 12,
                            }}
                        >
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 2l2.39 4.84 5.34.78-3.86 3.77.91 5.31L12 14.77 7.22 16.7l.91-5.31L4.27 7.62l5.34-.78L12 2z"
                                    stroke="#6524EB"
                                    strokeWidth="1.7"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h3
                            id="top-tier-title"
                            style={{
                                margin: "0 0 10px",
                                fontSize: 18,
                                fontWeight: 700,
                                color: "#24252C",
                            }}
                        >
                            You're on the highest plan
                        </h3>
                        <p
                            style={{
                                margin: "0 0 20px",
                                fontSize: 14,
                                color: "#6E6A7C",
                                lineHeight: 1.5,
                            }}
                        >
                            No higher tier is available right now. Need a custom
                            setup? Contact{" "}
                            <a
                                href="mailto:support@rxpt.us"
                                style={{ color: "#6524EB", fontWeight: 600 }}
                            >
                                support@rxpt.us
                            </a>
                            .
                        </p>
                        <button
                            type="button"
                            onClick={handleTopTierModalClose}
                            style={{
                                width: "100%",
                                padding: "14px",
                                background: "#6524EB",
                                border: "none",
                                borderRadius: 14,
                                color: "#fff",
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: "pointer",
                                boxShadow:
                                    "0 6px 18px -4px rgba(101, 36, 235, 0.45)",
                            }}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default RecommendedPlan;

export { PLAN_DEFS, COMPARISON_ROWS, COMPARISON_COLUMNS };
